import {
  getColumns,
  HEADER_ID,
  itemCheckedWithSet,
  TableBody,
  TableBulkItemHeader,
  TableBulkItemRow,
  TableHeaderItem,
  TableSearchEdit,
  toggleAll,
  toggleItemSingleOnlyWithSet,
  toggleItemWithSet
} from "collections/Table";
import { format } from "date-fns";
import { NoResultsIndicator, NoResultsIndicatorAnimation } from "elements";
import {
  ResourceFileFragment,
  useGetResourceFilesQuery
} from "generated/graphql";
import { infiniteNestLink, Switch } from "polyrouter";
import { useTenantId } from "polyrouter/SelectedTenantProvider";
import { ReactNode, useState } from "react";
import { useSelector } from "react-redux";
import { State } from "store";
import { TABLES, TABLE_COLUMN_STATE } from "store/userMeta";
import { ProtectedNavLink, ProtectedRoute } from "TsRoleGuards";
import { useTranslation } from "utilities/i18n";
import { handleNullFields } from "utilities/stringUtils";
import { generateTableHeaders } from "utilities/tableUtils";
import { Layout } from "views/Main/Layouts";
import { AddResourceFileModal } from "./Add/AddResourceFileModal";
import DeleteResourceFilesModal from "./Delete/DeleteResourceFilesModal";

const SIZE_UNITS = ["B", "KB", "MB", "GB"];
const BREAKING_SIZE = 1024;

type ResourceFileRowProps = Omit<ResourceFileFragment, "__typename"> & {
  shownColumns: Array<string>;
  onCheck: (id: string) => void;
  checked: boolean;
  showChecks: boolean;
  singleSelection?: boolean;
};

const ResourceFileRow: React.FC<ResourceFileRowProps> = ({
  fileName,
  fileSize,
  uploadedBy,
  uploadedTime,
  shownColumns,
  checked,
  onCheck,
  showChecks,
  singleSelection = false
}) => {
  const { COLUMNS } = TABLES.RESOURCE_FILES;

  let size = fileSize ?? 0;
  let i = 0;
  while (i < SIZE_UNITS.length && size >= BREAKING_SIZE) {
    size = +Math.fround(size / BREAKING_SIZE).toFixed(2);
    i++;
  }
  const sizeUnit = SIZE_UNITS[Math.min(i, SIZE_UNITS.length - 1)];
  const sizeString = `${size} ${sizeUnit}`;

  const getTableContent = (column: string) => {
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.FILE_NAME:
        content = handleNullFields(fileName);
        break;
      case COLUMNS.FILE_SIZE:
        content = sizeString;
        break;
      case COLUMNS.UPLOADED_BY:
        content = handleNullFields(uploadedBy, true);
        break;
      case COLUMNS.UPLOADED_TIME:
        content = uploadedTime
          ? format(new Date(uploadedTime), "Pp")
          : handleNullFields(uploadedTime, true);
    }

    return <td key={column}>{content}</td>;
  };

  return (
    <TableBulkItemRow
      checked={checked}
      onCheck={() => onCheck(fileName ?? "")}
      showChecks={showChecks}
      singleSelection={singleSelection}
      data-testid={`resource-file-row-${fileName}`}
    >
      {shownColumns.map(getTableContent)}
    </TableBulkItemRow>
  );
};

export const ResourceFilesTable: React.FC<{
  shownColumns: string[];
  hiddenColumns: string[];
  showEmptyState?: boolean;
  showMetrics?: boolean;
  hasActions?: boolean;
  inModal?: boolean;
  singleSelection?: boolean;
  onSelect?: (item: string) => void;
  selected?: string[];
}> = ({
  shownColumns,
  hiddenColumns,
  onSelect,
  showEmptyState = false,
  showMetrics = false,
  hasActions = false,
  inModal = false,
  singleSelection = false,
  selected = []
}) => {
  const { t } = useTranslation();
  const tenantId = useTenantId();
  const [selectedItems, setSelectedItems] = useState<string[]>(selected);
  const { NAME: TABLE_NAME, COLUMNS } = TABLES.RESOURCE_FILES;

  const { data, fetching } = useGetResourceFilesQuery({
    variables: {
      tenantId
    },
    pause: !tenantId
  });
  const resourceFiles =
    data?.getAllResourceFiles?.filter(
      (file): file is ResourceFileFragment => !!file
    ) ?? [];
  const resourceFilesCount = resourceFiles.length;

  const itemChecked = itemCheckedWithSet(selectedItems);
  const toggleItem = singleSelection
    ? toggleItemSingleOnlyWithSet(selectedItems, setSelectedItems)
    : toggleItemWithSet(resourceFilesCount, selectedItems, setSelectedItems);
  const selectedFiles = selectedItems.filter(item => item !== HEADER_ID);

  const deleteDisabled = selectedFiles.length === 0;
  const deleteBtnClass = ["btn", deleteDisabled && "disabled"]
    .filter(c => !!c)
    .join(" ");

  const columnTranslations = {
    [COLUMNS.FILE_NAME]: t("FILE_NAME"),
    [COLUMNS.UPLOADED_TIME]: t("DATE_UPLOADED"),
    [COLUMNS.UPLOADED_BY]: t("UPLOADED_BY"),
    [COLUMNS.FILE_SIZE]: t("SIZE")
  };
  const shown = getColumns(shownColumns, columnTranslations);
  const hidden = getColumns(hiddenColumns, columnTranslations);

  const getTableHeaders = (column: string, title: string) => (
    <TableHeaderItem fieldName={column} title={title} key={column} />
  );

  const tableContainerClasses = ["table-container", inModal && "overflow-table"]
    .filter(c => !!c)
    .join(" ");
  const tableClasses = ["table custom-table", inModal && "on-white"]
    .filter(c => !!c)
    .join(" ");

  return (
    <>
      {!fetching && resourceFilesCount === 0 && showEmptyState ? (
        <NoResultsIndicatorAnimation title={t("NO_RESOURCE_FILES")}>
          <ProtectedNavLink
            to={infiniteNestLink("/add")}
            requirement="add:device:resource:file"
            className="btn btn-primary"
          >
            {t("ADD")}
          </ProtectedNavLink>
        </NoResultsIndicatorAnimation>
      ) : (
        <>
          {showMetrics && (
            <div className="table-heading">
              <span className="metric" data-testid="files-count">
                {resourceFilesCount}
              </span>
              <span className="title ml-5">{t("RESOURCE_FILES")}</span>
            </div>
          )}
          {hasActions && (
            <div className="mb-15 table-actions">
              <ProtectedNavLink
                to={infiniteNestLink("/add")}
                requirement="add:device:resource:file"
                className="btn btn-primary"
              >
                {t("ADD")}
              </ProtectedNavLink>
              <ProtectedNavLink
                to={deleteDisabled ? "" : infiniteNestLink("/remove")}
                requirement="delete:device:resource:file"
                className={deleteBtnClass}
              >
                {deleteDisabled
                  ? t("REMOVE_RESOURCE_FILE")
                  : t("REMOVE_RESOURCE_FILE", {
                      count: selectedFiles.length
                    })}
              </ProtectedNavLink>
              <TableSearchEdit
                edit={{
                  tableName: TABLE_NAME,
                  shownColumns: shown,
                  hiddenColumns: hidden
                }}
              />
            </div>
          )}
          <div className={tableContainerClasses}>
            <table className={tableClasses}>
              <TableBulkItemHeader
                checked={itemChecked(HEADER_ID)}
                onCheck={() =>
                  toggleAll(
                    resourceFiles.map(file => file?.fileName || ""),
                    selectedItems,
                    setSelectedItems
                  )
                }
                showChecks={!!shownColumns.length}
                replaceCheckWith={singleSelection ? <span></span> : undefined}
              >
                {generateTableHeaders(shown, getTableHeaders)}
              </TableBulkItemHeader>
              <TableBody
                loading={fetching}
                cols={shown.length}
                accommodateScrollbar
              >
                {resourceFiles.map(resourceFile => (
                  <ResourceFileRow
                    {...resourceFile}
                    onCheck={() => {
                      const item = resourceFile?.fileName ?? "";
                      toggleItem(item);
                      onSelect && onSelect(item);
                    }}
                    checked={itemChecked(resourceFile?.fileName ?? "")}
                    showChecks={!!shownColumns.length}
                    singleSelection={singleSelection}
                    shownColumns={shown.map(column => column.key)}
                    key={resourceFile.fileName}
                  />
                ))}
                {!fetching && resourceFilesCount === 0 ? (
                  <tr>
                    <td>
                      <NoResultsIndicator
                        className="inline mt-5"
                        title={t("NO_RESOURCE_FILES")}
                      />
                    </td>
                  </tr>
                ) : null}
              </TableBody>
            </table>
          </div>
        </>
      )}
      <Switch>
        <ProtectedRoute
          nest
          path="/remove"
          requirement="delete:device:resource:file"
        >
          <DeleteResourceFilesModal
            resourceFiles={selectedFiles}
            onFinish={() => setSelectedItems([])}
          />
        </ProtectedRoute>
        <ProtectedRoute nest path="/add" requirement="add:device:resource:file">
          <AddResourceFileModal />
        </ProtectedRoute>
      </Switch>
    </>
  );
};

export const ResourceFiles: React.FC = () => {
  const { NAME: TABLE_NAME } = TABLES.RESOURCE_FILES;
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });

  const { Content } = Layout;
  return (
    <Layout>
      <Content className="no-sidebar">
        <ResourceFilesTable
          shownColumns={shownColumns}
          hiddenColumns={hiddenColumns}
          showEmptyState
          showMetrics
          hasActions
        />
      </Content>
    </Layout>
  );
};
